// Global Instructions Rule Applied!
//
// HTTP layer for Configuration → Stage Actions. Talks to the Python backend
// only; every read row is Zod-validated before it reaches the screen (invalid
// rows are dropped with a dev warning, never coerced).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import type { RouteDefinition } from '../../../core/utils/endpoints.ts'
import {
  StageActionAdminModel,
  StageActionTypeModel,
  validateStageActionAdmin,
  validateStageActionType
} from '../../../state_manager/data_models/stage_action_model.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface WriteResponse {
  success: boolean
  data?: unknown
  error?: string
}

/** Fields the backend allow-lists for a cfg.stage_actions write. On update,
 *  include the recipient lists to REPLACE the respective recipient set (omit
 *  to leave it untouched). The recipient_* lists are the To header; the
 *  cc_recipient_* lists the Cc header. */
export interface StageActionSavePayload {
  pipeline_config_id?: string
  action_type?: string
  email_design_template_id?: string | null
  params?: Record<string, unknown>
  enabled?: boolean
  recipient_user_ids?: string[]
  /** Dynamic deal-team roles: 'lead' | 'sourced_by' | 'introduced_by' */
  recipient_roles?: string[]
  cc_recipient_user_ids?: string[]
  cc_recipient_roles?: string[]
}

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ message?: string }>(response)
  return body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

function validateRows<T>(rawRows: unknown[], validate: (row: unknown) => T, label: string): T[] {
  const out: T[] = []
  for (const row of rawRows) {
    try {
      out.push(validate(row))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn(`${label} row failed validation:`, e, row)
    }
  }
  return out
}

async function readList<T>(
  route: RouteDefinition,
  validate: (row: unknown) => T,
  label: string,
  accessToken: string | null
): Promise<{ data: T[]; error?: string }> {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route), {
      method: route.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) return { data: [], error: await errorMessage(response, `Failed to fetch ${label}`) }
    const json = (await response.json()) as { data?: unknown[] }
    return { data: validateRows(Array.isArray(json.data) ? json.data : [], validate, label) }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'Unexpected error' }
  }
}

async function write(url: string, method: string, accessToken: string | null, body?: unknown): Promise<WriteResponse> {
  try {
    const response = await fetch(url, {
      method,
      headers: authHeaders(accessToken),
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    if (!response.ok) return { success: false, error: await errorMessage(response, 'Request failed') }
    const json = (await response.json()) as { data?: unknown }
    return { success: true, data: json.data }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchStageActions = (
  accessToken: string | null
): Promise<{ data: StageActionAdminModel[]; error?: string }> =>
  readList(API_ROUTES.CONFIG.STAGE_ACTIONS_LIST, validateStageActionAdmin, 'Stage action', accessToken)

export const fetchStageActionTypes = (
  accessToken: string | null
): Promise<{ data: StageActionTypeModel[]; error?: string }> =>
  readList(API_ROUTES.CONFIG.STAGE_ACTION_TYPES, validateStageActionType, 'Stage action type', accessToken)

export const createStageAction = (payload: StageActionSavePayload, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_ACTIONS_CREATE),
    API_ROUTES.CONFIG.STAGE_ACTIONS_CREATE.method,
    token,
    { data: payload }
  )

export const updateStageAction = (
  stageActionId: string,
  payload: StageActionSavePayload,
  token: string | null
): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_ACTIONS_UPDATE, { stageActionId }),
    API_ROUTES.CONFIG.STAGE_ACTIONS_UPDATE.method,
    token,
    { data: payload }
  )

export const deleteStageAction = (stageActionId: string, token: string | null): Promise<WriteResponse> =>
  write(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.STAGE_ACTIONS_DELETE, { stageActionId }),
    API_ROUTES.CONFIG.STAGE_ACTIONS_DELETE.method,
    token
  )
